import React from 'react';
import { ChevronLeft, ChevronRight, Check, AlertTriangle, Loader2, Flag } from 'lucide-react';
import { useEstimationForm } from '../../../contexts/EstimationFormContext';
import { DETAILED_STEPS } from './config';

const SaveIndicator: React.FC<{ status: string }> = ({ status }) => {
  if (status === 'idle') return null;

  const configs = {
    saving: {
      icon: <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />,
      label: 'Enregistrement...',
      color: '#7C6FA0',
    },
    saved: {
      icon: <Check size={11} strokeWidth={2.5} />,
      label: 'Enregistré',
      color: '#059669',
    },
    error: {
      icon: <AlertTriangle size={11} strokeWidth={2} />,
      label: "Erreur d'enregistrement",
      color: '#D97706',
    },
  } as const;

  const cfg = configs[status as keyof typeof configs];
  if (!cfg) return null;

  return (
    <span
      className="flex items-center gap-1"
      style={{ fontSize: 11, color: cfg.color, flexShrink: 0, whiteSpace: 'nowrap' }}
    >
      {cfg.icon}
      {cfg.label}
    </span>
  );
};

const FormStepNav: React.FC = () => {
  const ctx = useEstimationForm();
  if (!ctx) return null;

  const { currentStep, stepIsValid, stepNavRef, onStepClickRef, autosaveStatus } = ctx;
  const stepIndex = DETAILED_STEPS.findIndex(s => s.id === currentStep);
  const stepNumber = stepIndex + 1;
  const totalSteps = DETAILED_STEPS.length;
  const progressPercent = (stepNumber / totalSteps) * 100;
  const hasBack = stepIndex > 0;
  const resultStep = DETAILED_STEPS[DETAILED_STEPS.length - 1].id;
  const isLastStep = currentStep === resultStep;
  // Show "J'ai terminé" on all steps except the result step itself,
  // and only once the description step is validated (stepIndex > 0).
  const showFinish = !isLastStep && stepIndex > 0;

  return (
    <>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <div
        className="flex items-center gap-3 px-4 shrink-0"
        style={{
          height: 44,
          borderBottom: '0.5px solid var(--color-border)',
          background: 'white',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <span style={{ fontSize: 12, color: '#7C6FA0', whiteSpace: 'nowrap', flexShrink: 0 }}>
          Étape {stepNumber} / {totalSteps}
        </span>

        <div style={{ width: 70, height: 3, background: 'rgba(0,0,0,0.08)', borderRadius: 99, flexShrink: 0 }}>
          <div
            style={{
              height: '100%',
              width: `${progressPercent}%`,
              background: 'var(--gradient-accent)',
              borderRadius: 99,
              transition: 'width 0.3s ease',
            }}
          />
        </div>

        <SaveIndicator status={autosaveStatus} />

        <div style={{ flex: 1 }} />

        <button
          onClick={() => stepNavRef.current.onBack?.()}
          disabled={!hasBack}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 12,
            padding: '4px 10px',
            borderRadius: 'var(--radius-sm)',
            border: '0.5px solid var(--color-border)',
            background: 'white',
            color: hasBack ? '#1E1148' : '#C4B5D0',
            cursor: hasBack ? 'pointer' : 'default',
          }}
        >
          <ChevronLeft size={14} />
          Précédent
        </button>

        <button
          onClick={() => stepNavRef.current.onNext?.()}
          disabled={!stepIsValid}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 12,
            padding: '4px 12px',
            borderRadius: 'var(--radius-sm)',
            border: 'none',
            background: stepIsValid ? 'var(--gradient-primary)' : 'rgba(0,0,0,0.08)',
            color: stepIsValid ? 'white' : '#B0A0C0',
            cursor: stepIsValid ? 'pointer' : 'default',
          }}
        >
          Suivant
          <ChevronRight size={14} />
        </button>

        {showFinish && (
          <button
            onClick={() => onStepClickRef.current(resultStep)}
            title="Voir le résultat de l'estimation"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 12,
              padding: '4px 10px',
              borderRadius: 'var(--radius-sm)',
              border: '0.5px solid #E07B39',
              background: 'white',
              color: '#E07B39',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            <Flag size={13} strokeWidth={2} />
            J'ai terminé
          </button>
        )}
      </div>
    </>
  );
};

export default FormStepNav;
